import { Box, CircularProgress } from "@mui/material";
import { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { addStepComment, getEscalationThread } from "../api";
import { EscalationThread } from "../dtos";
import CommentsPanel from "./comments-panel";

/** Module-local rather than exported: a second export here costs the file its Fast Refresh boundary. */
const escalationThreadKey = (stepUid: string): string[] => [
  "nawy-now",
  "escalation-thread",
  stepUid,
];

/**
 * §Escalate-thread: the escalation conversation on a step the agent cannot open.
 *
 * The full comment thread lives inside StepForm, which renders only for a step the agent can WRITE. That
 * is the whole gap: the agent escalates a step that is stuck on somebody else — a Credit Review step is
 * the normal case — and the answer lands somewhere they have no route to. They were notified, the row
 * says "Escalated", the server would accept their reply, and there was nothing on screen to reply to.
 *
 * Fetched separately rather than folded into the step detail, because the step detail is exactly what
 * 403s here. It is fetched only when the card is open, so a board of forty steps costs nothing.
 */
const EscalationThreadPanel = ({
  stepUid,
  caseUid,
  emptyFallback,
}: {
  stepUid: string;
  caseUid: string;
  /**
   * §Escalate-thread-board: what to render instead of nothing when the thread comes back empty.
   *
   * Reachable, not theoretical: `escalateStep` only writes a comment when a note was given, so a step
   * escalated by the ESCALATE side-effect — or through any path that passed no note — is flagged with an
   * empty thread behind it. Inline in a step card that is the right answer (render nothing, the card has
   * other content); in a dialog opened BY the flag it is a blank box, so that host passes a sentence.
   */
  emptyFallback?: ReactNode;
}) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: escalationThreadKey(stepUid),
    queryFn: () => getEscalationThread(stepUid),
  });

  const reply = useMutation({
    mutationFn: ({ content, parentUid, attachmentUids }: { content: string; parentUid?: string; attachmentUids?: string[] }) =>
      addStepComment(stepUid, content, parentUid, attachmentUids),
    // Awaited rather than fired and forgotten: react-query keeps the mutation pending until this settles, so
    // `busy` covers the refetch too and the composer cannot be used again against a thread still reloading.
    onSuccess: async () => {
      toast.success("Reply sent");
      await queryClient.invalidateQueries({ queryKey: escalationThreadKey(stepUid) });
    },
    // The server's own copy, which distinguishes "not yours to reply to" from a step-level refusal.
    onError: (error: { response?: { data?: { message?: string } } }) =>
      toast.error(error?.response?.data?.message ?? "Could not send the reply"),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  const thread: EscalationThread | undefined = data;
  if (!thread?.comments?.length) return emptyFallback ?? null;

  return (
    <CommentsPanel
      comments={thread.comments}
      caseUid={caseUid}
      stepUid={stepUid}
      // The server's answer, never a guess: reading this thread is granted to anyone who can see the
      // lead, replying only to the escalation's own parties. Offering a composer the POST would refuse
      // costs the agent a typed paragraph and tells them nothing.
      editable={thread.canReply}
      busy={reply.isLoading}
      onPost={(content, parentUid, attachmentUids) =>
        reply.mutateAsync({ content, parentUid, attachmentUids })
      }
      title="Escalation"
      replyOnly
    />
  );
};

export default EscalationThreadPanel;
